import React, { useEffect } from 'react';

import ErrorContainer from 'container/Error';
import { useAppDispatch } from 'store/hooks';
import { setPageTranslation } from 'store/systems';

const Home = React.lazy(() => import('pages/Home'));
const AboutUs = React.lazy(() => import('pages/AboutUs'));
const LeaderShip = React.lazy(() => import('pages/LeaderShip'));
const Contact = React.lazy(() => import('pages/Contact'));
const NotFound = React.lazy(() => import('pages/NotFound'));
const LandscapeSolutions = React.lazy(() => import('pages/LandscapeSolutions'));
const FAQ = React.lazy(() => import('pages/FAQ'));
const Privacy = React.lazy(() => import('pages/Privacy'));
const Term = React.lazy(() => import('pages/Term'));
const FieldActivity = React.lazy(() => import('pages/FieldActivity'));
const HightechAgriculture = React.lazy(() => import('pages/HightechAgriculture'));
const FarmTourism = React.lazy(() => import('pages/FarmTourism'));
const Strategy = React.lazy(() => import('pages/Strategy'));
const WorkingEnvironment = React.lazy(() => import('pages/WorkingEnvironment'));
const PositionRecruitment = React.lazy(() => import('pages/PositionRecruitment'));
const News = React.lazy(() => import('pages/News'));
const Libraries = React.lazy(() => import('pages/Libraries'));
const Videos = React.lazy(() => import('pages/Videos'));
const ShareholderRelations = React.lazy(() => import('pages/ShareholderRelations'));
const StockPriceInvestRelation = React.lazy(() => import('pages/InvestmentRelationship/stockPrice'));
const MessageInvestRelation = React.lazy(() => import('pages/InvestmentRelationship/message'));
const CardSmallInvestRelation = React.lazy(() => import('pages/InvestmentRelationship/cardSmall'));
const PdfInvestRelation = React.lazy(() => import('pages/InvestmentRelationship/pdf'));
const CardLargeInvestRelation = React.lazy(() => import('pages/InvestmentRelationship/cardLarge'));

export type TemplateCodeTypes = {
  code: string;
  component: React.FC<BasePageDataTypes<any>>;
}

export const errorTemplateCode = (status?: number) => {
  switch (status) {
    case 400:
      return 'ERROR_400';

    case 403:
      return 'ERROR_403';

    case 404:
      return 'ERROR_404';

    default:
      return 'ERROR_500';
  }
};

export const TemplateCode: TemplateCodeTypes[] = [
  {
    code: 'HOME',
    component: Home,
  },
  {
    code: 'ABOUT_US',
    component: AboutUs,
  },
  {
    code: 'MANAGER_SYSTEM',
    component: LeaderShip,
  },
  {
    code: 'CONTACT',
    component: Contact,
  },
  {
    code: 'page404',
    component: NotFound,
  },
  {
    code: 'LANDSCAPE_SOLUTION',
    component: LandscapeSolutions,
  },
  {
    code: 'FAQ',
    component: FAQ,
  },
  {
    code: 'PRIVACY',
    component: Privacy,
  },
  {
    code: 'CUSTOMER_TERM',
    component: Term,
  },
  {
    code: 'FIELD_ACTIVITY',
    component: FieldActivity,
  },
  {
    code: 'HIGH_TECH_AGRICULTURE',
    component: HightechAgriculture,
  },
  {
    code: 'FARM_TRAVEL',
    component: FarmTourism, // FertilizersProbiotics
  },
  {
    code: 'DEVELOPMENT_STRATEGY',
    component: Strategy,
  },
  {
    code: 'JOB_ENVIRONMENT',
    component: WorkingEnvironment,
  },
  {
    code: 'POSITION_RECRUITMENT',
    component: PositionRecruitment,
  },
  {
    code: 'MEDIA',
    component: News,
  },
  {
    code: 'LIBRARIES',
    component: Libraries,
  },
  {
    code: 'VIDEO_NEWS',
    component: Videos,
  },
  {
    code: 'SHAREHOLDER_RELATION',
    component: ShareholderRelations,
  },
  {
    code: 'STOCKS_MARKET_INFORMATION',
    component: StockPriceInvestRelation,
  },
  {
    code: 'DOCUMENT_COMPANY',
    component: MessageInvestRelation,
  },
  {
    code: 'EVENT_CALENDAR',
    component: CardSmallInvestRelation,
  },
  {
    code: 'INFORMATION_COMPANY',
    component: PdfInvestRelation,
  },
  {
    code: 'NEWS_MARKET',
    component: CardLargeInvestRelation,
  },
];

export const RenderPage: React.FC<Record<'data', BasePageDataTypes<any> | undefined>> = ({ data }) => {
  const dispatch = useAppDispatch();
  useEffect(() => {
    if (data) {
      dispatch(setPageTranslation(data.pageData.translations));
    }
    return () => {
      dispatch(setPageTranslation(undefined));
    };
  }, [data, dispatch]);

  if (!data) return null;

  const Component = TemplateCode?.find((t) => t.code === data?.pageData?.templateCode)?.component;

  if (Component) {
    return React.createElement<BasePageDataTypes<any>>(Component, data);
  }

  return <ErrorContainer statusCode={404} />;
};
